import type { CustomerSummary, Order } from '@morales/shared';
import type { UseQueryResult } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiError } from '../api';
import { useOnline, useTiers } from '../hooks';
import { useT } from '../i18n';
import { IconBack, IconPhone } from './icons';

export function OfflineBanner() {
  const online = useOnline();
  const t = useT();
  if (online) return null;
  return (
    <div className="offline-banner" role="status">
      {t('offline')}
    </div>
  );
}

/**
 * Until JL switches (decision 86) QuickBooks is the book of record: invoices, payments, credits and bills typed in
 * the app are practice and are cleared when the QuickBooks invoices arrive. Shown where money is entered; never
 * printed. When the switch is built this reads the setting and disappears after it.
 */
export function PracticeBanner({ kind = 'sales' }: { kind?: 'sales' | 'bills' }) {
  const t = useT();
  return (
    <p className="practice-band no-print" role="note" data-testid="practice-banner">
      <strong>{t('practiceTitle')}</strong> {kind === 'bills' ? t('practiceBills') : t('practiceSales')}
    </p>
  );
}

export function Loading() {
  const t = useT();
  return (
    <p className="muted center" role="status">
      {t('loading')}
    </p>
  );
}

export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const t = useT();
  const status = error instanceof ApiError ? error.status : undefined;
  // a refusal the server explains (409 "reconnect QuickBooks", 422 "below cost") is said in its words, not "something went wrong";
  // so is an outside service that failed (502/503/504: "the reader's key was refused", "split the PDF") and a limit
  // reached (429): the person can act on them. A 400/401/403/404/500 keeps the plain message
  const said = error instanceof ApiError && !error.unreadable && [409, 422, 429, 502, 503, 504].includes(status ?? 0) ? (error.problem.detail ?? null) : null;
  // a page or a button that is not for this role says so plainly (decision 184), with the server's reason after it
  if (status === 403) {
    const why = error instanceof ApiError && !error.unreadable ? (error.problem.detail ?? null) : null;
    return (
      <div className="alert alert-error" role="alert" data-testid="not-for-role">
        <p>
          {t('notForRole')}
          {why ? <span className="muted"> ({why})</span> : null}
        </p>
      </div>
    );
  }
  return (
    <div className="alert alert-error" role="alert">
      <p>
        {said ?? (status === 0 ? t('offline') : t('error'))}
        {status && !said ? <span className="muted"> ({status})</span> : null}
      </p>
      {onRetry ? (
        <button type="button" className="btn btn-secondary" onClick={onRetry}>
          {t('retry')}
        </button>
      ) : null}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="empty">{children}</p>;
}

/** Data if we have any (even stale while offline), else the error, else a loading line. */
export function QueryView<T>({ query, children }: { query: UseQueryResult<T, Error>; children: (data: T) => ReactNode }) {
  if (query.data !== undefined) return <>{children(query.data)}</>;
  if (query.isError) return <ErrorBox error={query.error} onRetry={() => void query.refetch()} />;
  return <Loading />;
}

export function BackButton({ fallback, className }: { fallback: string; className?: string | undefined }) {
  const navigate = useNavigate();
  const location = useLocation();
  const t = useT();
  return (
    <button
      type="button"
      className={className ? `icon-btn ${className}` : 'icon-btn'}
      aria-label={t('back')}
      onClick={() => (location.key !== 'default' ? navigate(-1) : navigate(fallback))}
    >
      <IconBack />
    </button>
  );
}

export function PageHead({ title, back }: { title: ReactNode; back?: string }) {
  return (
    <div className="page-head">
      {/* on the desk the sidebar is the way home, so the arrow back to Home hides there */}
      {back ? <BackButton fallback={back} className={back === '/' ? 'back-home' : undefined} /> : null}
      <h1>{title}</h1>
    </div>
  );
}

export function SearchBox({ value, onChange, autoFocus = false }: { value: string; onChange: (v: string) => void; autoFocus?: boolean }) {
  const t = useT();
  return (
    <input
      className="search"
      type="search"
      inputMode="search"
      enterKeyHint="search"
      autoComplete="off"
      autoCorrect="off"
      spellCheck={false}
      autoFocus={autoFocus}
      placeholder={t('search')}
      aria-label={t('search')}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export function TierBadge({ tier }: { tier: CustomerSummary['priceTier'] }) {
  const { nameOf } = useTiers();
  return <span className="badge">{nameOf(tier)}</span>;
}

export function CallAheadBadge() {
  const t = useT();
  return (
    <span className="badge badge-warn">
      <IconPhone /> {t('callAhead')}
    </span>
  );
}

export function InactiveBadge() {
  const t = useT();
  return <span className="badge badge-danger">{t('inactive')}</span>;
}

export function StatusBadge({ status }: { status: Order['status'] }) {
  const t = useT();
  return <span className={`badge status-${status}`}>{t(`status_${status}`)}</span>;
}
